import bannerPlaceholder from "assets/images/evento-banner.jpg";
import { Close } from "assets/svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { TQuizQuestion, TQuizResult } from "services/types";
import { useEventStore, useGuestStore } from "store";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { clarity } from "react-microsoft-clarity";
import {
  clearQuizProgress,
  confettiShoot,
  getQuizProgress,
  saveQuizProgress,
  Timer,
} from "./gamesUtils";
import TopBar from "pages/FeedV2/TopBar";
import GuestFormModal from "components/GuestFormModal";
import CreatePost from "pages/FeedV2/CreatePost";
import { useFeedStore } from "store";
import { TFeedEvent } from "cdt-app-public-api/dist/types";

export default function Quiz() {
  const { event, theme } = useEventStore();
  const {
    guest,
    games: { quizResult },
    setQuizResult,
    setGameStatus,
  } = useGuestStore();
  const [activeQuestion, setActiveQuestion] = useState<TQuizQuestion | null>(
    null
  );
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [answers, setAnswers] = useState<{ [key: string]: string }>(
    quizResult?.answers || {}
  );
  const [saving, setSaving] = useState(false);
  const scoreRef = useRef<number>(quizResult?.score || 0);
  const timerRef = useRef<number>(quizResult?.timeSpentInSeconds || 0);
  const navigate = useNavigate();
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [shouldStartAfterModal, setShouldStartAfterModal] = useState(false);

  useEffect(() => {
    if (event?.id && guest?.id && !quizResult) {
      const savedProgress = getQuizProgress(event.id, guest.id);

      if (savedProgress) {
        setAnswers(savedProgress.answers);
        scoreRef.current = savedProgress.score;
        timerRef.current = savedProgress.timeSpent;
      }
    }
  }, [event?.id, guest?.id, quizResult]);

  const sortedQuestions = useMemo(() => {
    if (!event || !event.id || !event.quiz) return [];
    if (!guest?.id) return [];

    const savedProgress = getQuizProgress(event.id, guest.id);
    if (!quizResult && savedProgress) {
      return savedProgress.questions;
    }

    const shuffled = [...event.quiz].sort(() => Math.random() - 0.5);

    if (!quizResult) {
      saveQuizProgress(event.id, guest.id, {
        answers: {},
        currentQuestionIndex: 0,
        score: 0,
        timeSpent: 0,
        questions: shuffled,
      });
    }

    return shuffled;
  }, [event, guest?.id, quizResult]);

  const totalQuestions = sortedQuestions.length;

  const activeQuestionIndex = useMemo(() => {
    return sortedQuestions.findIndex((q) => q.id === activeQuestion?.id);
  }, [sortedQuestions, activeQuestion]);

  const currentStep = useMemo(() => {
    if (saving) return "saving";
    if (quizResult) return "result";
    if (!activeQuestion) return "intro";

    return "question";
  }, [activeQuestion, quizResult, saving]);

  const saveResult = useCallback(async () => {
    setSaving(true);

    const quizResult: TQuizResult = {
      eventId: event?.id as string,
      eventCode: event?.code as string,
      guestId: guest?.id as string,
      guestName: guest?.name,
      guestPhotoStoragePath: guest?.photoStoragePath || null,
      quiz: sortedQuestions,
      answers,
      score: scoreRef.current,
      totalQuestions,
      timeSpentInSeconds: timerRef.current,
      createdAt: new Date().toISOString(),
    };

    const cdtAppClient = new CdtAppPublicApi();
    const quizResultCreated = await cdtAppClient.createQuizResult(quizResult);

    setQuizResult(quizResultCreated as TQuizResult);

    if (event?.id && guest?.id) {
      clearQuizProgress(event.id, guest.id);
    }

    if (clarity.hasStarted()) {
      clarity.setTag("engagement_level_creator", "true");
    }

    setSaving(false);

    // confetti effect
    setGameStatus("quiz_congrats_seen", true);
    setTimeout(() => confettiShoot(), 1000);
  }, [
    event,
    guest,
    sortedQuestions,
    answers,
    totalQuestions,
    setQuizResult,
    setGameStatus,
  ]);

  const handleOptionClick = useCallback((optionValue: string) => {
    setSelectedOption(optionValue);
  }, []);

  const handleConfirmAnswer = useCallback(() => {
    if (!activeQuestion || !selectedOption || !event?.id || !guest?.id) return;

    const newAnswers = {
      ...answers,
      [activeQuestion.id]: selectedOption,
    };

    setAnswers(newAnswers);
    scoreRef.current += activeQuestion.correct === selectedOption ? 1 : 0;

    const nextQuestionIndex = activeQuestionIndex + 1;
    const nextQuestion = sortedQuestions[nextQuestionIndex];

    if (nextQuestion) {
      saveQuizProgress(event.id, guest.id, {
        answers: newAnswers,
        currentQuestionIndex: nextQuestionIndex,
        score: scoreRef.current,
        timeSpent: timerRef.current,
        questions: sortedQuestions,
      });
    }

    setSelectedOption(null);
    setActiveQuestion(nextQuestion);

    if (!nextQuestion) {
      saveResult();
      clearQuizProgress(event.id, guest.id);
    }
  }, [
    activeQuestion,
    selectedOption,
    sortedQuestions,
    activeQuestionIndex,
    saveResult,
    answers,
    event?.id,
    guest?.id,
  ]);

  const onStartClick = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (!guest?.name) {
      setGuestModalOpen(true);
      setShouldStartAfterModal(true);
      return;
    }

    const savedProgress = getQuizProgress(event.id, guest.id);

    if (!savedProgress) return;

    const currentQuestion =
      savedProgress.questions[savedProgress.currentQuestionIndex];
    if (currentQuestion) {
      setActiveQuestion(currentQuestion);
      return;
    }

    setActiveQuestion(sortedQuestions[0]);
  }, [sortedQuestions, event?.id, guest?.id, guest?.name]);

  const handleGuestModalClose = useCallback(() => {
    setGuestModalOpen(false);

    if (shouldStartAfterModal && event?.id && guest?.id) {
      setShouldStartAfterModal(false);

      const savedProgress = getQuizProgress(event.id, guest.id);

      if (!savedProgress) return;

      const currentQuestion =
        savedProgress.questions[savedProgress.currentQuestionIndex];
      if (currentQuestion) {
        setActiveQuestion(currentQuestion);
        return;
      }

      setActiveQuestion(sortedQuestions[0]);
    }
  }, [shouldStartAfterModal, event?.id, guest?.id, sortedQuestions]);

  const handleGameClose = useCallback(() => {
    if (!event?.id || !guest?.id) return;
    if (activeQuestion && Object.keys(answers).length > 0) {
      saveQuizProgress(event.id, guest.id, {
        answers,
        currentQuestionIndex: activeQuestionIndex,
        score: scoreRef.current,
        timeSpent: timerRef.current,
        questions: sortedQuestions,
      });
    }

    setActiveQuestion(null);
    setAnswers({});
    setSelectedOption(null);
    scoreRef.current = 0;

    if (event?.betaFeatures?.includes("feed")) {
      return navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  }, [
    navigate,
    event?.betaFeatures,
    activeQuestion,
    event?.id,
    guest?.id,
    answers,
    activeQuestionIndex,
    sortedQuestions,
  ]);

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("game_quiz", "engaged");
    }
  }, []);

  return (
    <div
      className={`${theme} h-full w-full flex justify-center items-center px-5 py-16`}
    >
      <div className="h-full w-full flex flex-col justify-start items-center">
        {/* Header */}
        <div className="flex w-full items-center justify-between mb-4">
          <h1 className="m-0 p-0 grow text-center">
            <FormattedMessage id="title.quiz" defaultMessage="Quiz" />
          </h1>
          {event?.betaFeatures?.includes("feed") && (
            <TopBar onClose={handleGameClose} />
          )}
          {currentStep !== "intro" &&
            !event?.betaFeatures?.includes("feed") && (
              <button
                className="flex items-center text-primary fixed right-3 top-3"
                onClick={handleGameClose}
              >
                <span className="text-lg">
                  <Close
                    width={36}
                    height={36}
                    color="none"
                    outline="#414141"
                  />
                </span>
              </button>
            )}
        </div>

        {currentStep === "intro" && <Intro onStartClick={onStartClick} />}

        {currentStep === "question" && activeQuestion && (
          <>
            {/* Subheader */}
            <div className="mb-8 flex justify-between w-full">
              {/* Question number */}
              <div>
                <span className="text-xl">
                  <span className="font-black text-black">
                    {activeQuestionIndex + 1}
                  </span>
                  /<span className="text-gray-500">{totalQuestions}</span>
                </span>
              </div>
              <Timer timerRef={timerRef} gameType="quiz" />
            </div>
            <Question
              key={activeQuestion.id}
              question={activeQuestion}
              selectedOption={selectedOption}
              onOptionClick={handleOptionClick}
            />
            {/* confirm button */}
            <div className="w-full mt-8 flex justify-center">
              <button
                onClick={handleConfirmAnswer}
                disabled={!selectedOption}
                className={`px-8 py-3 font-medium ${
                  selectedOption
                    ? `${theme.button}`
                    : `${theme.button} !bg-gray-200 !text-gray-400 cursor-not-allowed`
                }`}
              >
                <FormattedMessage
                  id="button.quiz.confirm"
                  defaultMessage="Confirmar"
                />
              </button>
            </div>
          </>
        )}

        {currentStep === "saving" && (
          <p className="text-center">
            <FormattedMessage
              id="message.quiz.saving"
              defaultMessage="Salvando..."
            />
          </p>
        )}

        {currentStep === "result" && (
          <Result
            score={scoreRef.current}
            totalQuestions={totalQuestions}
            timeSpent={timerRef.current}
          />
        )}
      </div>
      <GuestFormModal isOpen={guestModalOpen} onClose={handleGuestModalClose} />
    </div>
  );
}

const Intro = ({ onStartClick }: { onStartClick: () => void }) => {
  const { theme } = useEventStore();
  const [countdown, setCountdown] = useState(3);
  const [isDisabled, setIsDisabled] = useState(true);
  const { guest, getGameStatus, setGameStatus } = useGuestStore();
  const hasSeenIntro = getGameStatus("quiz_intro_seen");
  const { event } = useEventStore();

  const hasProgress = useMemo(() => {
    if (event?.id && guest?.id) {
      const savedProgress = getQuizProgress(event.id, guest.id);
      return savedProgress && Object.keys(savedProgress.answers).length > 0;
    }
    return false;
  }, [event?.id, guest?.id]);

  useEffect(() => {
    if (hasSeenIntro) {
      setIsDisabled(false);
      setCountdown(0);
      return;
    }

    if (countdown === 0) {
      setIsDisabled(false);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, hasSeenIntro]);

  const handleStartClick = () => {
    if (!hasSeenIntro) {
      setGameStatus("quiz_intro_seen", true);
    }
    onStartClick();
  };

  return (
    <div className="flex flex-col justify-center items-center gap-4">
      {event?.customization?.theme.name === "rose" ? (
        <div className="relative wedding-rose">
          <img
            className="aspect-square object-cover w-full h-full"
            src={event?.bannerUrl || bannerPlaceholder}
            alt="banner-home"
          />
        </div>
      ) : (
        <div className="relative bg-white p-2 shadow-md">
          <img
            src={event?.bannerUrl || bannerPlaceholder}
            alt="event banner"
            className="aspect-[1/1.15] object-cover max-w-full"
          />
        </div>
      )}
      <h2 className="text-2xl font-medium text-center">
        <FormattedMessage
          id="subtitle.quiz.intro"
          defaultMessage="Topa o desafio?"
        />
      </h2>

      {hasProgress ? (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.quiz.progress"
            defaultMessage="O quiz ainda tá rolando! Continue e mostre que manda bem!"
          />
        </p>
      ) : (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.quiz.intro"
            defaultMessage="Entre todos os convidados, prove que vocé é a pessoa que mais conhece o
        casal!"
          />
        </p>
      )}

      <div className="w-full flex flex-col justify-center items-center gap-8">
        <button
          onClick={handleStartClick}
          className={`px-10 py-3 ${theme.button}`}
          disabled={isDisabled}
        >
          {hasSeenIntro && (
            <span className="text-center w-full">
              <FormattedMessage
                id="button.quiz.continue"
                defaultMessage="Continuar"
              />
            </span>
          )}
          {!hasSeenIntro && (
            <span className="text-center w-full">
              <FormattedMessage
                id="button.quiz.start"
                defaultMessage="Iniciar"
              />
            </span>
          )}

          {isDisabled && (
            <span className=" ml-2 animate-pulse">{countdown}</span>
          )}
        </button>
      </div>
    </div>
  );
};

type TQuestionProps = {
  question: TQuizQuestion;
  selectedOption: string | null;
  onOptionClick: (optionValue: string) => void;
};

const Question = (props: TQuestionProps) => {
  const { question, selectedOption, onOptionClick } = props;
  const { theme } = useEventStore();

  const unsortedOptions = useMemo(() => {
    return [...question.options].sort(() => Math.random() - 0.5);
  }, [question.options]);

  const handleOptionClick = useCallback(
    (optionId: number) => {
      const value = unsortedOptions[optionId];
      onOptionClick(value);
    },
    [unsortedOptions, onOptionClick]
  );

  return (
    <>
      {/* Question */}
      <div className={`${theme.card} card w-full p-6 mb-8`}>
        <h2 className="text-xl font-medium text-center">{question.text}</h2>
      </div>

      {/* Options */}
      <div className="gap-4 flex flex-col justify-start items-start w-full">
        {unsortedOptions.map((option, index) => {
          const buttonLetter = String.fromCharCode(65 + index);
          const isSelected = selectedOption === option;

          return (
            <button
              key={index}
              onClick={() => handleOptionClick(index)}
              className={`w-full px-4 py-3 flex items-center justify-start gap-4 relative overflow-hidden transform transition-all  ${
                isSelected
                  ? theme.name === "rose"
                    ? `${theme.button} shadow-lg !border-solid !border-2 scale-105`
                    : `${theme.buttonSecondary} !bg-gray-300 !text-black shadow-lg scale-105`
                  : theme.name === "rose"
                  ? `${theme.buttonSecondary}`
                  : `${theme.button}`
              }`}
            >
              <div
                className={`inline-flex items-center justify-center rounded-full grow-0 shrink-0 w-8 h-8 z-10 transition-all  transform ${
                  isSelected
                    ? theme.name === "rose"
                      ? "bg-rose-300 scale-110"
                      : "bg-black scale-110"
                    : theme.name === "rose"
                    ? "bg-primary-rose"
                    : "bg-black"
                }`}
              >
                <span className="text-center text-lg font-century-gothic-bold text-white">
                  {buttonLetter}
                </span>
              </div>

              <span
                className={`w-full text-left text-[14px] font-century-gothic z-10 transition-all ${
                  isSelected ? "font-semibold transform translate-x-2" : ""
                }`}
              >
                {option}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
};

const Result = (props: {
  score: number;
  totalQuestions: number;
  timeSpent: number;
}) => {
  const { score, totalQuestions, timeSpent } = props;
  const { event, theme } = useEventStore();
  const [createPost, setCreatePost] = useState(false);
  const { addOptimisticPost } = useFeedStore();
  const navigate = useNavigate();

  const handleClosePostCreation = () => {
    setCreatePost(false);
  };

  const handlePostCreation = (post: TFeedEvent) => {
    if (post && event?.code) {
      addOptimisticPost(event.code, post);
    }
    setCreatePost(false);
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <h2 className="text-2xl font-medium">
        <FormattedMessage
          id="subtitle.quiz.result"
          defaultMessage="Resultado"
        />
      </h2>
      <div className="py-4 text-center">
        {score >= totalQuestions / 2 && (
          <p className="text-gray-500 mt-2 mb-8" onClick={confettiShoot}>
            <FormattedMessage
              id="message.quiz.congrats"
              defaultMessage="🎉 Parabéns! 🎉"
            />
          </p>
        )}
        <span className="text-lg">
          <FormattedMessage
            id="message.quiz.result"
            defaultMessage={`Você acertou {score} de {totalQuestions} perguntas em {time} segundos`}
            values={{
              score: score,
              totalQuestions: totalQuestions,
              time: timeSpent,
            }}
          />
        </span>
        {event?.betaFeatures?.includes("feed") && (
          <div className="mt-8">
            <button
              className={`px-8 py-3 mt-4 ${theme?.buttonSecondary}`}
              onClick={() => setCreatePost(true)}
            >
              <FormattedMessage
                id="button.quiz.share"
                defaultMessage="Compartilhar Resultado"
              />
            </button>
          </div>
        )}
      </div>

      <CreatePost
        createPost={createPost}
        setPost={setCreatePost}
        onClose={handleClosePostCreation}
        onPostCreated={handlePostCreation}
        frameStyle="quiz"
        gameTag="quiz"
        gameResultText={`Acertei ${score} de ${totalQuestions} no quiz em ${timeSpent} segundos! E você, consegue fazer melhor?✨`}
      />
    </div>
  );
};
